export interface AdaptiveThresholds {
  range: unknown;
  advanceThreshold: unknown;
  fallbackThreshold: unknown;
}

export type AdaptiveThresholdErrors = Partial<Record<keyof AdaptiveThresholds, string>>;

const LABELS: Record<keyof AdaptiveThresholds, string> = {
  range: 'Adaptive range',
  advanceThreshold: 'Adaptive advance threshold',
  fallbackThreshold: 'Adaptive fallback threshold'
};

const FIELDS = Object.keys(LABELS) as (keyof AdaptiveThresholds)[];
const THRESHOLDS = ['advanceThreshold', 'fallbackThreshold'] as const;

/** A blank setting is not set at this layer, so it inherits from the layer above. */
export const isBlank = (value: unknown) => value === null || value === undefined || value === '';

const isWholeNumberOfOneOrMore = (value: unknown) =>
  Number.isInteger(Number(value)) && Number(value) >= 1;

/**
 * One message per field practice cannot run with: a value that is not a whole number of 1 or
 * more, or a threshold above the range. A blank value inherits, unless `requireAll` asks for
 * all three or none.
 */
export const getAdaptiveThresholdErrors = (
  values: AdaptiveThresholds,
  {requireAll = false}: {requireAll?: boolean} = {}
): AdaptiveThresholdErrors => {
  const errors: AdaptiveThresholdErrors = {};

  FIELDS.forEach((field) => {
    if (!isBlank(values[field]) && !isWholeNumberOfOneOrMore(values[field])) {
      errors[field] = `${LABELS[field]} must be a whole number of 1 or more.`;
    }
  });

  THRESHOLDS.forEach((field) => {
    const comparable = [field, 'range' as const].every(
      (key) => !isBlank(values[key]) && !errors[key]
    );
    if (comparable && Number(values[field]) > Number(values.range)) {
      errors[field] = `${LABELS[field]} must be less than or equal to the adaptive range.`;
    }
  });

  const blank = FIELDS.filter((field) => isBlank(values[field]));
  if (requireAll && blank.length > 0 && blank.length < FIELDS.length) {
    blank.forEach((field) => {
      errors[field] =
        'Set the range, advance threshold, and fallback threshold together, or leave all three blank.';
    });
  }

  return errors;
};
